import { Archive } from "lucide-react"
import { useArchiveThread } from "@/hooks/useChat"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import { ROUTES } from "@/routes/path"
import { Thread } from "@/types/api"

interface ArchiveThreadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  threadToArchive: Thread | null
  currentThreadId?: string | null
}

export function ArchiveThreadDialog({
  open,
  onOpenChange,
  threadToArchive,
  currentThreadId,
}: ArchiveThreadDialogProps) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const archiveThreadMutation = useArchiveThread({
    onSuccess: () => {
      // Refresh threads list
      queryClient.invalidateQueries({ queryKey: ["threads"] })

      // Navigate to studio index if the archived thread was the current one
      if (currentThreadId && threadToArchive && currentThreadId === threadToArchive.id) {
        navigate(ROUTES.DASHBOARD.STUDIO.INDEX)
      }

      onOpenChange(false)
    },
    onError: (error) => {
      console.error("Failed to archive thread:", error)
      // You could add a toast notification here
    },
  })

  const confirmArchive = () => {
    if (threadToArchive) {
      archiveThreadMutation.mutate(threadToArchive.id)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl overflow-visible p-0">
        <div className="flex items-start justify-between px-6 pt-6">
          <div className="rounded-lg border border-gray-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900">
            <Archive className="h-6 w-6 text-amber-500" />
          </div>
        </div>
        <DialogHeader className="px-6 pt-1">
          <DialogTitle className="text-2xl font-semibold">Archive conversation</DialogTitle>
          <DialogDescription className="mt-2">
            Are you sure you want to archive &ldquo;{threadToArchive?.name}&rdquo;? This conversation will be moved to
            your archived conversations and can be restored later.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-end gap-6 px-6 pb-5 pt-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={archiveThreadMutation.isPending}
            size="xs"
            className="text-primary-600 bg-transparent px-3 font-normal shadow-none hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            size="xs"
            onClick={confirmArchive}
            disabled={archiveThreadMutation.isPending}
            className="min-w-[80px] border-amber-200 text-amber-700 hover:bg-amber-50 dark:border-amber-800 dark:text-amber-300 dark:hover:bg-amber-900/20"
          >
            {archiveThreadMutation.isPending ? "Archiving..." : "Archive"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
